import React, { FunctionComponent, useRef, useState } from 'react'
import { Typography, Button, Input, Space, Table, InputRef } from 'antd'
import { formatTimestamp } from '../../utils/time'
import { Link, useLoaderData, useMatch, useRouter } from '@tanstack/react-router'
import { VehicleType, VehicleLocation } from 'server'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import type { ColumnType } from 'antd/es/table'
import type { FilterConfirmProps } from 'antd/es/table/interface'
import { arrayToMap } from '../../utils/data'
import { Vehicle, VehicleCategory } from 'server'
import moment from 'moment-timezone'

type DataIndex = keyof VehicleLocation

const VehiclesTable: FunctionComponent<any> = () => {
  const match = useMatch({ strict: false })
  const { vehicles, vehicleLocations, agencies, vehicleTypes, vehicleCategories } = useLoaderData({
    from: match.routeId ?? '/'
  })

  const router = useRouter()

  const agencyMap = arrayToMap(agencies, 'id')
  const vehicleTypeMap = arrayToMap(vehicleTypes, 'id')
  const vehicleCategoryMap = arrayToMap(vehicleCategories, 'vehicle_category_id')
  const vehicleMap = arrayToMap(vehicles, 'vehicle_id')

  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef<InputRef>(null)

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex.toString())
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Vehicle> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex.toString()}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false })
              setSearchText((selectedKeys as string[])[0])
              setSearchedColumn(dataIndex.toString())
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
    onFilter: (value, record) => {
      const vehicle = vehicleMap.get(Number(record.vehicle_id))
      if (!vehicle[dataIndex] && vehicle[dataIndex] !== '') {
        console.warn('dataIndex not found', dataIndex)
        return false
      }
      return vehicle[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase())
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      )
  })

  const columns = [
    {
      title: 'ID',
      dataIndex: 'vehicle_id',
      key: 'vehicle_id'
    },
    {
      title: 'Unit Name',
      // dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
      sorter: (a: VehicleLocation, b: VehicleLocation) => {
        const aVehicle = vehicleMap.get(Number(a.vehicle_id))
        const bVehicle = vehicleMap.get(Number(b.vehicle_id))
        return aVehicle.name.localeCompare(bVehicle.name)
      },
      render: (text: string, record: any, index: number) => {
        const vehicle = vehicleMap.get(Number(record.vehicle_id))
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={vehicle?.name || ''}
          />
        )
      }
    },
    {
      title: 'Asset ID',
      // dataIndex: 'asset_id',
      key: 'asset_id',
      ...getColumnSearchProps('asset_id'),
      render: (text: string, record: any, index: number) => {
        const vehicle = vehicleMap.get(Number(record.vehicle_id))
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={vehicle?.asset_id || ''}
          />
        )
      }
    },
    {
      title: 'Vehicle Category',
      // dataIndex: 'vehicle_category_id',
      key: 'vehicle_category_id',
      filters: vehicleCategories.map((category: VehicleCategory) => ({
        text: category.name,
        value: category.vehicle_category_id
      })),
      onFilter: (value: any, record: any) => {
        const vehicle = vehicleMap.get(Number(record.vehicle_id))
        return vehicle.vehicle_category_id === value
      },
      render: (text: string, record: any, index: number) => {
        const vehicle = vehicleMap.get(Number(record.vehicle_id))
        const vehicleCategory = vehicleCategoryMap.get(Number(vehicle.vehicle_category_id))
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={vehicleCategory.name || ''}
          />
        )
      }
    },
    {
      title: 'Vehicle Type',
      // dataIndex: 'vehicle_type_id',
      key: 'vehicle_type_id',
      filters: vehicleTypes.map((type: VehicleType) => ({
        text: type.name,
        value: type.id
      })),
      onFilter: (value: any, record: any) => {
        const vehicle = vehicleMap.get(Number(record.vehicle_id))
        return vehicle.vehicle_type_id === value
      },
      render: (text: string, record: any, index: number) => {
        const vehicle = vehicleMap.get(Number(record.vehicle_id))
        const vehicleType: VehicleType | undefined = vehicleTypeMap.get(Number(vehicle.vehicle_type_id))
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={vehicleType.name || ''}
          />
        )
      }
    },
    {
      title: 'Agency',
      // dataIndex: 'agency_id',
      key: 'agency_id',
      filters: agencies.map((agency: any) => ({
        text: agency.friendly_name || agency.name,
        value: agency.id
      })),
      onFilter: (value: any, record: any) => {
        const vehicle = vehicleMap.get(Number(record.vehicle_id))
        return vehicle.agency_id === value
      },
      render: (text: string, record: any, index: number) => {
        const vehicle = vehicleMap.get(Number(record.vehicle_id))
        const agency = agencyMap.get(Number(vehicle.agency_id))
        return (
          <Typography.Text
            onClick={(e: any) => {
              console.log('clicked')
              e.stopPropagation()
            }}
            ellipsis={true}
          >
            <Link to={`/admin/agencies/${agency?.id}`}>
              <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]}
                autoEscape
                textToHighlight={agency ? agency.name : ''}
              />
            </Link>
          </Typography.Text>
        )
      }
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a: VehicleLocation, b: VehicleLocation) => {
        return moment(a.created_at).unix() - moment(b.created_at).unix()
      },
      render: (text: string, record: any, index: number) => {
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={formatTimestamp(text) || ''}
          />
        )
      }
    },
    {
      title: 'Location Updated',
      key: 'location_updated_at',
      sorter: (a: VehicleLocation, b: VehicleLocation) => {
        const aU = moment(a.update_received).unix()
        const bU = moment(b.update_received).unix()
        return aU - bU
      },
      defaultSortOrder: 'descend' as const,
      render: (text: string, record: VehicleLocation, index: number) => {
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={formatTimestamp(record.update_received)}
          />
        )
      }
    }
  ]

  return (
    <Table
      style={{
        cursor: 'pointer',
        margin: '12px'
      }}
      pagination={{ pageSize: 100 }}
      onRow={(record, rowIndex) => {
        return {
          onClick: (event) => {
            const url = router.buildLocation({ to: `/admin/devices/${record.vehicle_id}` }).href;
            window.open(url, '_blank'); // Opens in a new tab
          }, // click row
        }
      }}
      bordered={true}
      size={'small'}
      dataSource={vehicleLocations}
      columns={columns}
      rowKey={'vehicle_id'}
      tableLayout={'fixed'}
    />
  )
}

export default VehiclesTable
